import { Component, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { CHIPS_VIEW, ListService } from '@shared/modules/list/services/list.service';
import { BaseListFiltersComponentComponent } from '@shared/components/base-list-filters-component/base-list-filters-component.component';
import { Config } from '@shared/configs/config';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Employee } from '@shared/models/employee.model';
import { ListEvent, ListEventType } from '@shared/modules/list/model/list-event.model';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { ProjectChangesStatus } from '@modules/projects/shared/enums/project-changes-status.enum';
import { ProjectChangesSettlementStatus } from '@modules/projects/shared/enums/project-change-settlement-status.enum';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { MatDialogRef } from '@angular/material/dialog';
import { DateRangePickerV2Component } from '@shared/components/date-rangepicker-v2/date-rangepicker-v2.component';
import { ChangeType } from '@shared/modules/task-sidenav/enums/change-type.enum';
import { tap } from 'rxjs';

enum EProjectChangeAmountType {
  INCREASE = 'increase',
  DECREASE = 'decrease',
  NO_PRICE = 'no-price',
  NOT_ESTIMATED = 'not-estimated'
}

@Component({
  selector: 'project-changes-filters',
  templateUrl: './project-changes-filters.component.html'
})
export class ProjectChangesFiltersComponent
  extends BaseListFiltersComponentComponent
  implements OnInit, OnDestroy
{
  Config = Config;
  ButtonSize = ButtonSize;
  ButtonTypes = ButtonTypes;

  statuses = [];
  types = [];
  settlements = [];

  protected readonly changeTypes = Object.values(ChangeType).map((key) => ({
    id: key as ChangeType,
    name: this.t.instant(`Projects.changeType.${key}`)
  })) as Array<{ id: ChangeType; name: string }>;

  typeControl: UntypedFormControl = new UntypedFormControl(null);

  constructor(
    @Optional() @Inject(CHIPS_VIEW) public chipsView: boolean,
    public service: ListService,
    @Optional() private dialogRef: MatDialogRef<ProjectChangesFiltersComponent>
  ) {
    super(service);
  }

  @ViewChild('daterangepicker') daterangepicker: DateRangePickerV2Component;

  ngOnInit(): void {
    this.createForm();
    this.setStatuses();
    this.setTypes();
    this.setSettlements();

    if (!this.chipsView) this.service.readUrlParams(false);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  listenQueryParams() {
    this.sub = this.service.eventEmitter
      .pipe(
        tap(({ type, data }: ListEvent<any>) => {
          if (type === ListEventType.QUERY_PARAMS) {
            if (isNotNullOrUndefined(data['filters[a.price][eq]'])) {
              this.typeControl.setValue(EProjectChangeAmountType.NO_PRICE);
            }

            if (isNotNullOrUndefined(data['filters[a.price][gt]'])) {
              this.typeControl.setValue(EProjectChangeAmountType.INCREASE);
            }

            if (isNotNullOrUndefined(data['filters[a.price][lt]'])) {
              this.typeControl.setValue(EProjectChangeAmountType.DECREASE);
            }

            if (isNotNullOrUndefined(data['filters[a.isPriceEstimated][eq]'])) {
              this.typeControl.setValue(EProjectChangeAmountType.NOT_ESTIMATED);
            }

            const creatorFilter = data['filters[a.creator][in]'];
            if (isNotNullOrUndefined(creatorFilter) && !Array.isArray(creatorFilter)) {
              const formattedArr = data['filters[a.creator][in]']?.split(',');
              data['filters[a.creator][in]'] = formattedArr;
            }

            const typeFilter = data['filters[a.changeType][in]'];
            if (isNotNullOrUndefined(typeFilter) && !Array.isArray(typeFilter)) {
              const formattedArr = data['filters[a.changeType][in]']?.split(',');
              data['filters[a.changeType][in]'] = formattedArr;
            }

            this.setFiltersToForm(data);
          }
        })
      )
      .subscribe();
  }

  createForm() {
    this.service.filtersForm = new UntypedFormGroup({
      '[a-creator][in]': new UntypedFormControl(null),
      '[a-changeStatus][eq]': new UntypedFormControl(null),
      '[a-settlementStatus][eq]': new UntypedFormControl(null),
      '[a-price][gt]': new UntypedFormControl(null),
      '[a-price][lt]': new UntypedFormControl(null),
      '[a-price][eq]': new UntypedFormControl(null),
      '[a-isPriceEstimated][eq]': new UntypedFormControl(null),
      '[a-termStart][gte]': new UntypedFormControl(null),
      '[a-termEnd][lte]': new UntypedFormControl(null),
      '[a-changeType][in]': new UntypedFormControl(null)
    });
  }

  setTypes() {
    this.types = [
      {
        id: EProjectChangeAmountType.INCREASE,
        name: this.t.instant('ProjectChanges.List.increaseChange')
      },
      {
        id: EProjectChangeAmountType.DECREASE,
        name: this.t.instant('ProjectChanges.List.decreaseChange')
      },
      {
        id: EProjectChangeAmountType.NO_PRICE,
        name: this.t.instant('ProjectChanges.List.noPriceChange')
      },
      {
        id: EProjectChangeAmountType.NOT_ESTIMATED,
        name: this.t.instant('ProjectChanges.List.withoutEstimation')
      }
    ];
  }

  setSettlements() {
    this.settlements = [];

    Object.values(ProjectChangesSettlementStatus).map((key) => {
      this.settlements.push({
        id: key,
        name: this.t.instant('Projects.ProjectSettlement.' + key)
      });
    });
  }

  setStatuses() {
    this.statuses = [];

    Object.values(ProjectChangesStatus).map((key) => {
      if (key === ProjectChangesStatus.CHANGE_STATUS_SETTLED && !!this.store.Employee.isClient) {
        return;
      }
      if (
        key !== ProjectChangesStatus.CHANGE_STATUS_EDITED &&
        key !== ProjectChangesStatus.CHANGE_STATUS_SETTLED
      ) {
        this.statuses.push({
          id: key,
          name: this.t.instant('Projects.ProjectChangeStatuses.' + key)
        });
      }
    });
  }

  onTypeChange() {
    this.service.filtersForm.get('[a-price][gt]').setValue(null);
    this.service.filtersForm.get('[a-price][lt]').setValue(null);
    this.service.filtersForm.get('[a-price][eq]').setValue(null);
    this.service.filtersForm.get('[a-isPriceEstimated][eq]').setValue(null);

    switch (this.typeControl.value) {
      case EProjectChangeAmountType.INCREASE:
        this.service.filtersForm.get('[a-price][gt]').setValue(0);
        break;
      case EProjectChangeAmountType.DECREASE:
        this.service.filtersForm.get('[a-price][lt]').setValue(0);
        break;
      case EProjectChangeAmountType.NO_PRICE:
        this.service.filtersForm.get('[a-price][eq]').setValue(0);
        this.service.filtersForm.get('[a-isPriceEstimated][eq]').setValue(1);
        break;
      case EProjectChangeAmountType.NOT_ESTIMATED:
        this.service.filtersForm.get('[a-isPriceEstimated][eq]').setValue(0);
        break;
    }

    this.onChangeFilter();
  }

  creatorName(employee: Employee) {
    const e = new Employee(employee);
    return e.userPerson.previewName;
  }

  clearFilters() {
    Object.keys(this.service?.filtersForm.value).forEach((key) => {
      this.service.filtersForm.get(key).setValue(null);
    });
    this.typeControl.setValue(null);
    this.daterangepicker.resetValue();
  }

  cancel() {
    this.dialogRef.close();
    this.refreshChips();
  }

  submit() {
    this.service.getRows();
    this.dialogRef.close();
    this.refreshChips();
  }
}
